import { useEffect, useRef } from 'react';

import { getElectronAPI, isElectron } from 'src/hooks/useElectron';
import { useAppSelector } from 'src/hooks/useStore';
import { NotificationGroups } from 'src/store/notifications/types';
import type { NativeNotificationPayload } from 'src/types/electron';

/**
 * Notification title mapping for different notification types.
 */
const NOTIFICATION_TITLES: Record<string, string> = {
  [NotificationGroups.FileNotifications]: 'New File',
  [NotificationGroups.EsigRequestNotifications]: 'Signature Request',
  [NotificationGroups.EsigRequestCompleteNotifications]: 'Document Signed',
  [NotificationGroups.FormCompleteNotifications]: 'Form Completed',
  [NotificationGroups.FormRequestNotifications]: 'Form Request',
  [NotificationGroups.NewInvoiceNotifications]: 'New Invoice',
  [NotificationGroups.FileUploadNotification]: 'File Uploaded',
  [NotificationGroups.InvoicePaidNotifcations]: 'Invoice Paid',
};

/**
 * Hook to sync Redux notification state with native Electron notifications.
 * Updates the dock badge count and can optionally trigger native notifications.
 */
export const useNativeNotifications = () => {
  const electronAPI = getElectronAPI();
  const previousCountRef = useRef<number>(0);

  // Get notification arrays from Redux
  const fileNotifications = useAppSelector(
    (state) => state.notifications.fileNotifications,
  );
  const esigRequestNotifications = useAppSelector(
    (state) => state.notifications.esigRequestNotifications,
  );
  const formRequestNotifications = useAppSelector(
    (state) => state.notifications.formRequestNotifications,
  );
  const newInvoiceNotifications = useAppSelector(
    (state) => state.notifications.newInvoiceNotifications,
  );

  // Calculate total unread count
  const totalUnread =
    fileNotifications.length +
    esigRequestNotifications.length +
    formRequestNotifications.length +
    newInvoiceNotifications.length;

  // Update dock badge when count changes
  useEffect(() => {
    if (!electronAPI) return;

    electronAPI.setBadgeCount(totalUnread);
    previousCountRef.current = totalUnread;
  }, [totalUnread, electronAPI]);

  return {
    totalUnread,
    isElectron: !!electronAPI,
  };
};

/**
 * Helper function to show a native notification from anywhere in the app.
 * Only works when running in Electron.
 */
export const showNativeNotification = (
  payload: NativeNotificationPayload,
): void => {
  if (!isElectron()) return;

  const electronAPI = getElectronAPI();
  electronAPI?.showNotification(payload);
};

/**
 * Helper to create a notification payload from a notification type.
 */
export const createNotificationPayload = (
  id: string,
  notificationType: NotificationGroups,
  body: string,
  metadata?: Record<string, unknown>,
): NativeNotificationPayload => {
  return {
    id,
    title: NOTIFICATION_TITLES[notificationType] ?? 'Notification',
    body,
    notificationType,
    metadata,
  };
};
